import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plus, Search, Phone, Mail, MapPin, Trash2, Upload, Download, LayoutGrid, List as ListIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const emptyForm = {
  name: "",
  phone: "",
  email: "",
  address: "",
  property_notes: "",
  service_notes: "",
};

const CSV_TEMPLATE = "name,phone,email,address,property_notes,service_notes\nJane Homeowner,678-555-0101,jane@example.com,\"123 Oak Ln, Loganville, GA\",Gate code 1234,Mow weekly\n";

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const importInput = useRef(null);
  const [view, setView] = useState(() => localStorage.getItem("bm_customers_view") || "grid");

  useEffect(() => { localStorage.setItem("bm_customers_view", view); }, [view]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customers");
      setCustomers(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/customers", form);
      toast.success("Customer added");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id, name) => {
    if (!window.confirm(`Delete ${name}? This also removes all their jobs.`)) return;
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Customer deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const filtered = customers.filter((c) => {
    const q = query.toLowerCase();
    return (
      !q ||
      c.name.toLowerCase().includes(q) ||
      (c.phone || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q) ||
      (c.address || "").toLowerCase().includes(q)
    );
  });

  return (
    <div data-testid="customers-page">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Customer Book</div>
          <h1 className="font-display text-4xl font-black tracking-tight leading-none">
            Customers <span className="text-[#8ba394] font-mono text-2xl ml-3">{customers.length}</span>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setImportOpen(true); setImportResult(null); }} className="bm-btn bm-btn-outline" data-testid="import-customers-button">
            <Upload className="w-4 h-4" /> Import CSV/Excel
          </button>
          <button onClick={() => setOpen(true)} className="bm-btn bm-btn-primary" data-testid="add-customer-button">
            <Plus className="w-4 h-4" /> Add Customer
          </button>
        </div>
      </div>

      <div className="bm-card p-4 mb-4 flex items-center gap-3">
        <Search className="w-4 h-4 text-[#8ba394]" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, phone, email, or address"
          className="bg-transparent outline-none flex-1 text-sm"
          data-testid="customer-search-input"
        />
        <div className="inline-flex border border-[#233129] rounded-sm overflow-hidden" data-testid="customers-view-toggle">
          <button
            onClick={() => setView("grid")}
            className={`px-2.5 py-1.5 transition-colors ${view === "grid" ? "bg-[#1e3b2d] text-[#eae6db]" : "text-[#8ba394] hover:text-[#eae6db]"}`}
            data-testid="customers-view-grid"
            title="Card view"
            aria-label="Card view"
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setView("list")}
            className={`px-2.5 py-1.5 transition-colors ${view === "list" ? "bg-[#1e3b2d] text-[#eae6db]" : "text-[#8ba394] hover:text-[#eae6db]"}`}
            data-testid="customers-view-list"
            title="List view"
            aria-label="List view"
          >
            <ListIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="overline">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bm-card p-10 text-center text-[#8ba394]" data-testid="customers-empty">
          <div className="mb-4">No customers match.</div>
          <button onClick={() => setOpen(true)} className="bm-btn bm-btn-outline">
            <Plus className="w-4 h-4" /> Add the first one
          </button>
        </div>
      ) : view === "list" ? (
        <div className="bm-card overflow-hidden" data-testid="customers-list">
          <table className="w-full">
            <thead>
              <tr className="text-left border-b border-[#233129]">
                <th className="overline p-4">Name</th>
                <th className="overline p-4">Phone</th>
                <th className="overline p-4">Address</th>
                <th className="p-4 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-b border-[#233129] hover:bg-[#1d2823] transition-colors" data-testid={`customer-row-${c.id}`}>
                  <td className="p-4">
                    <Link to={`/customers/${c.id}`} className="font-display font-bold hover:text-[#e56b42]">{c.name}</Link>
                  </td>
                  <td className="p-4 font-mono text-sm text-[#b0c0b6]">{c.phone || "—"}</td>
                  <td className="p-4 text-sm text-[#b0c0b6]">{c.address || "—"}</td>
                  <td className="p-4 text-right">
                    <button
                      onClick={() => onDelete(c.id, c.name)}
                      className="text-[#8ba394] hover:text-[#c83e2d]"
                      data-testid={`delete-customer-${c.id}`}
                      aria-label="Delete customer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="customers-grid">
          {filtered.map((c) => (
            <div key={c.id} className="bm-card bm-card-hover p-5 relative" data-testid={`customer-card-${c.id}`}>
              <Link to={`/customers/${c.id}`} className="block">
                <div className="font-display font-bold text-lg mb-3 pr-8">{c.name}</div>
                {c.phone && (
                  <div className="flex items-center gap-2 text-sm text-[#b0c0b6] mb-1">
                    <Phone className="w-3.5 h-3.5" /> <span className="font-mono">{c.phone}</span>
                  </div>
                )}
                {c.email && (
                  <div className="flex items-center gap-2 text-sm text-[#b0c0b6] mb-1">
                    <Mail className="w-3.5 h-3.5" /> <span className="truncate">{c.email}</span>
                  </div>
                )}
                {c.address && (
                  <div className="flex items-start gap-2 text-sm text-[#8ba394] mt-2">
                    <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>{c.address}</span>
                  </div>
                )}
              </Link>
              <button
                onClick={() => onDelete(c.id, c.name)}
                className="absolute top-3 right-3 p-1.5 rounded-sm text-[#8ba394] hover:text-[#c83e2d] hover:bg-[#0a0f0d]"
                data-testid={`delete-customer-${c.id}`}
                aria-label="Delete customer"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141c18] border border-[#233129] text-[#eae6db] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black">New Customer</DialogTitle>
            <DialogDescription className="text-[#8ba394] text-sm">
              Add contact information and property details to your customer book.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onCreate} className="space-y-3" data-testid="new-customer-form">
            <div>
              <label className="overline block mb-2">Name *</label>
              <input required name="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bm-input" data-testid="customer-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline block mb-2">Phone</label>
                <input name="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bm-input" data-testid="customer-phone-input" />
              </div>
              <div>
                <label className="overline block mb-2">Email</label>
                <input name="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bm-input" data-testid="customer-email-input" />
              </div>
            </div>
            <div>
              <label className="overline block mb-2">Address</label>
              <input name="address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bm-input" data-testid="customer-address-input" />
            </div>
            <div>
              <label className="overline block mb-2">Property Notes</label>
              <textarea rows={2} value={form.property_notes} onChange={(e) => setForm({ ...form, property_notes: e.target.value })} className="bm-input" placeholder="Gate code, terrain, pets..." />
            </div>
            <div>
              <label className="overline block mb-2">Service Notes</label>
              <textarea rows={2} value={form.service_notes} onChange={(e) => setForm({ ...form, service_notes: e.target.value })} className="bm-input" placeholder="Standing preferences, pricing notes..." />
            </div>
            <DialogFooter className="mt-4">
              <button type="button" onClick={() => setOpen(false)} className="bm-btn bm-btn-outline">Cancel</button>
              <button type="submit" disabled={saving} className="bm-btn bm-btn-primary" data-testid="customer-submit-button">
                {saving ? "Saving…" : "Save Customer"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="bg-[#141c18] border border-[#233129] text-[#eae6db] max-w-lg" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black">Import Customers</DialogTitle>
            <DialogDescription className="text-[#8ba394] text-sm">
              Upload a CSV or Excel file. Required column: <span className="font-mono text-[#eae6db]">name</span>. Optional:
              <span className="font-mono text-[#eae6db]"> phone, email, address, property_notes, service_notes</span>.
            </DialogDescription>
          </DialogHeader>

          <div className="bm-card p-3 text-xs text-[#8ba394] flex items-center justify-between">
            <span>Need a starting point?</span>
            <button
              type="button"
              onClick={() => {
                const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "barmann-customer-template.csv";
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="bm-btn bm-btn-ghost text-xs"
              data-testid="download-template-button"
            >
              <Download className="w-3.5 h-3.5" /> Download template
            </button>
          </div>

          <input
            ref={importInput}
            type="file"
            accept=".csv,.xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
            className="hidden"
            data-testid="import-file-input"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setImporting(true);
              setImportResult(null);
              try {
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await api.post("/customers/import", fd);
                setImportResult(data);
                toast.success(`${data.created} customers imported`);
                load();
              } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail));
              } finally {
                setImporting(false);
                if (importInput.current) importInput.current.value = "";
              }
            }}
          />

          <button
            type="button"
            onClick={() => importInput.current?.click()}
            disabled={importing}
            className="bm-btn bm-btn-primary w-full"
            data-testid="import-pick-file-button"
          >
            <Upload className="w-4 h-4" /> {importing ? "Importing…" : "Choose a CSV or Excel file"}
          </button>

          {importResult && (
            <div className="bm-card p-4 text-sm space-y-1" data-testid="import-result">
              <div className="flex justify-between">
                <span className="text-[#8ba394]">Rows in file</span>
                <span className="font-mono">{importResult.total_rows}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#8ba394]">Imported</span>
                <span className="font-mono text-[#2d6a4f] font-bold">{importResult.created}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#8ba394]">Skipped (empty rows)</span>
                <span className="font-mono">{importResult.skipped}</span>
              </div>
              {importResult.errors.length > 0 && (
                <div className="pt-2">
                  <div className="text-[#e56b42] text-xs">Errors:</div>
                  <ul className="text-xs text-[#b0c0b6] list-disc pl-4">
                    {importResult.errors.map((e, i) => (<li key={i}>{e}</li>))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <button type="button" onClick={() => setImportOpen(false)} className="bm-btn bm-btn-outline">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
